import {
  AnyFunc,
  ErrorCodes,
  EventEmitter,
  HttpServerSentEventClient,
  Logger,
  createError,
  createEventEmitter,
  createHttpServerSentEventClient,
  eventObject,
} from '@quickplay/foundation';
import {
  AdMetadata,
  AdType,
  TrackMetadata,
  TritonStreamSidebandMetadata,
  TritonStreamSidebandMetadataEvent,
} from '../model/TritonStreamSidebandMetadata';
import { Action, Player, StreamTimelineEvent } from '@quickplay/player-interface';
import { v4 as uuid } from 'uuid';
import { SidebandMetadataUtil } from './SidebandMetadataUtil';
import { TritonAudioStreamConnectionConfig } from '../../TritonAudioStreamConnection';
import { TritonErrorCodes } from '../../internal/TritonErrorCodes';

export class SideBandMetadataAdapter {
  private readonly SIDEBAND_METADATA_ID = '~sbmid~'; // placeholder string for side band session Id
  private readonly RETRY_DELAY_RANGE_LOWER_BOUNDARY = 1000; // lower boundary for SSE retry delay.
  private readonly RETRY_DELAY_RANGE_UPPER_BOUNDARY = 5000; // upper boundary for SSE retry delay.
  private readonly DEFAULT_RETRY = 3; // default retry count for SSE failure.
  private sseClientAdapter_: HttpServerSentEventClient;
  private sessionId_: string;
  private activeTrackMetadata_: TrackMetadata | undefined;
  private isPlayingAd_ = false;
  private activeAdMetadata_: AdMetadata | undefined;
  private cuePointMetadataContainer_ = new Map<number, TritonStreamSidebandMetadata>();
  private player_: Player | undefined;
  private retryCount: number;
  private emitter_: EventEmitter = createEventEmitter();

  constructor(
    private contentUrl: string,
    private audioStreamConnectionConfig: TritonAudioStreamConnectionConfig,
    private logger_: Logger,
  ) {
    this.sessionId_ = uuid();
    this.retryCount = audioStreamConnectionConfig.sidebandMetadataConnectionRetries ?? this.DEFAULT_RETRY;
    this.sseClientAdapter_ = createHttpServerSentEventClient(this.getsidebandMetadataUrl());
    this.subscribeToServerSentEvents();
  }

  subscribe(eventName: string | eventObject, callback?: AnyFunc): void {
    this.emitter_.subscribe(eventName, callback);
  }

  unsubscribe(eventName: string | eventObject, callback?: AnyFunc): void {
    this.emitter_.unsubscribe(eventName, callback);
  }

  init(player: Player): void {
    this.player_ = player;
    this.sseClientAdapter_.init();
  }

  release(): void {
    this.sseClientAdapter_.release();
  }

  getContentUrl(): string {
    return this.contentUrl.replace(this.SIDEBAND_METADATA_ID, this.sessionId_);
  }
  checkTritonStreamSbmMetadata(): void {
    if (!this.player_) {
      return;
    }
    const currentTime = Math.ceil(this.player_.currentTime);
    const currentPlayingTrack = this.cuePointMetadataContainer_.get(currentTime);

    if (currentPlayingTrack) {
      this.processSidebandMetadata(currentPlayingTrack);
      this.cuePointMetadataContainer_.delete(currentTime);
    }
  }

  private handleStreamTimelineEvent(
    event: TritonStreamSidebandMetadataEvent,
    visibility: boolean,
    metadata: any,
  ): void {
    if (!this.player_) {
      return;
    }
    if (event === TritonStreamSidebandMetadataEvent.AD) {
      this.player_.send(StreamTimelineEvent.TRITON_AD, visibility ? Action.DISPLAY : Action.HIDE, metadata);
    } else if (event === TritonStreamSidebandMetadataEvent.TRACK) {
      this.player_.send(
        StreamTimelineEvent.TRITON_AUDIO_MEDIA_TRACK,
        visibility ? Action.DISPLAY : Action.HIDE,
        metadata,
      );
    } else if (event === TritonStreamSidebandMetadataEvent.SPEECH) {
      this.player_.send(
        StreamTimelineEvent.TRITON_AUDIO_MEDIA_SPEECH,
        visibility ? Action.DISPLAY : Action.HIDE,
        metadata,
      );
    } else {
      this.player_.send(StreamTimelineEvent.TRITON_SWEEPER, visibility ? Action.DISPLAY : Action.HIDE, metadata);
    }
  }

  private subscribeToServerSentEvents(): void {
    this.sseClientAdapter_.subscribe('open', (_event: any) => {
      this.logger_.debug('Connection Opened');
    });
    this.sseClientAdapter_.subscribe('error', (_event: any) => {
      this.retryOnError();
    });
    this.sseClientAdapter_.subscribe('message', (event: any) => {
      const data: TritonStreamSidebandMetadata = this.getTritonStreamSidebandMetadata(event);
      if (data.name === TritonStreamSidebandMetadataEvent.HLS) {
        return;
      }
      this.handleTritonCuePoint(data);
    });
  }

  private handleTritonCuePoint(metadata: TritonStreamSidebandMetadata): void {
    if (metadata.name === TritonStreamSidebandMetadataEvent.HLS) {
      return;
    }

    switch (metadata.name) {
      case TritonStreamSidebandMetadataEvent.TRACK:
      case TritonStreamSidebandMetadataEvent.SPEECH:
      case TritonStreamSidebandMetadataEvent.AD:
      case TritonStreamSidebandMetadataEvent.SWEEPER:
        if (metadata.timestampMs === 0) {
          this.processSidebandMetadata(metadata);
        }
    }
    this.cuePointMetadataContainer_.set(Math.ceil(metadata.timestampMs / 1000), metadata);
  }

  /**
   * * process sideband metadata.
   * @param sbmMetadata
   */
  private processSidebandMetadata(sbmMetadata: TritonStreamSidebandMetadata): void {
    if (sbmMetadata.name === TritonStreamSidebandMetadataEvent.AD) {
      this.processAdCuePointMetadata(sbmMetadata);
    } else {
      // send ad ended event to application if the last played track is ad.
      if (this.isPlayingAd_ && this.activeAdMetadata_) {
        this.sendAdEndedEvent();
      }
      // send ad ended event to application if the last played track is non-ad.
      if (this.activeTrackMetadata_) {
        this.sendTrackEndedEvent();
      }
      this.processNonAdCuePointMetadata(sbmMetadata);
    }
  }

  /**
   * Signals application that current Ad has ended and resets the active ad infomation.
   */
  private sendAdEndedEvent(): void {
    this.handleStreamTimelineEvent(TritonStreamSidebandMetadataEvent.AD, false, this.activeAdMetadata_);
    this.activeAdMetadata_ = undefined;
    this.isPlayingAd_ = false;
  }

  /**
   * Signals applications that current Ad has started.
   */
  private sendAdStartedEvent(sbmMetadata: TritonStreamSidebandMetadata): void {
    // signals application active track is ended before signalling ad start.
    if (this.activeTrackMetadata_) {
      this.sendTrackEndedEvent();
    }
    // signals application active ad is ended before signalling new ad start.
    if (this.isPlayingAd_ && this.activeAdMetadata_) {
      this.sendAdEndedEvent();
    }
    this.isPlayingAd_ = true;
    this.activeAdMetadata_ = SidebandMetadataUtil.buildAdMetadata(sbmMetadata);
    this.handleStreamTimelineEvent(TritonStreamSidebandMetadataEvent.AD, true, this.activeAdMetadata_);
  }

  /**
   * process ad cuepoint metadata.
   * @param metadata TritonStreamSidebandMetadata
   */
  private processAdCuePointMetadata(metadata: TritonStreamSidebandMetadata): void {
    // Signals applications that current Ad has ended.
    // Note:
    // As mentioned in triton documentation {@link https://help.tritondigital.com/docs/ad-delimiters ad-delimiters},
    // Break periods ends are marked either explicitly, by an “end break” marker,
    // or implicitly, by reception of a cue point that is not specifically identified as an ad.
    if (
      metadata.cuePointParameters.ad_type &&
      this.isPlayingAd_ &&
      metadata.cuePointParameters.ad_type === AdType.END_BREAK
    ) {
      this.sendAdEndedEvent();
    }
    // Signals applications that current Ad has ended.
    // Note:
    // As mentioned in triton documentation {@link https://help.tritondigital.com/docs/ad-delimiters ad-delimiters},
    // Individual ads, and entire break periods (i.e., a series of contiguous ads), are triggered by the same cue point.
    // When the player receives such a cue point, it should consider the stream as “in break,” and adjust its behavior accordingly(e.g., display / un - hide banners, clear the artist / title information, etc.).
    // The player is considered “in break” until it receives one of the “Break End” cue points described below.
    else if (metadata.cuePointParameters.ad_type && metadata.cuePointParameters.ad_type !== AdType.END_BREAK) {
      this.sendAdStartedEvent(metadata);
    }
  }

  /**
   * process non ad cuepoint metadata.
   * @param metadata TritonStreamSidebandMetadata
   */
  private processNonAdCuePointMetadata(metadata: TritonStreamSidebandMetadata): void {
    // process track metadata
    if (metadata.name === TritonStreamSidebandMetadataEvent.TRACK) {
      this.activeTrackMetadata_ = {
        name: TritonStreamSidebandMetadataEvent.TRACK,
        metadata: SidebandMetadataUtil.buildTrackMetadata(metadata),
      };
    }
    // process speech metadata
    else if (metadata.name === TritonStreamSidebandMetadataEvent.SPEECH) {
      this.activeTrackMetadata_ = {
        name: TritonStreamSidebandMetadataEvent.SPEECH,
        metadata: SidebandMetadataUtil.buildTrackMetadata(metadata),
      };
    }
    // process sweeper metadata
    else {
      this.activeTrackMetadata_ = {
        name: TritonStreamSidebandMetadataEvent.SWEEPER,
        metadata: SidebandMetadataUtil.buildSweeperMetadata(metadata),
      };
    }

    this.handleStreamTimelineEvent(this.activeTrackMetadata_.name, true, this.activeTrackMetadata_.metadata);
  }

  /**
   * signals application that current playing track is ended.
   */
  private sendTrackEndedEvent(): void {
    if (!this.activeTrackMetadata_) {
      return;
    }
    this.handleStreamTimelineEvent(this.activeTrackMetadata_.name, false, this.activeTrackMetadata_.metadata);
    this.activeTrackMetadata_ = undefined;
  }

  /**
   * builds TritonStreamSidebandMetadata from server send event metadata.
   * @param event server sent event.
   * @returns TritonStreamSidebandMetadata
   */
  private getTritonStreamSidebandMetadata(event: any): TritonStreamSidebandMetadata {
    const data: any = JSON.parse(event.data);
    return {
      type: data.type,
      name: data.name,
      timestampMs: data.timestamp,
      cuePointParameters: data.parameters,
    };
  }

  /**
   * creates sideband metadata url using sessionId which is required to establish connection to triton sideband servers.
   * @returns string
   */
  private getsidebandMetadataUrl(): string {
    return this.audioStreamConnectionConfig.sidebandMetadataUrl.replace(this.SIDEBAND_METADATA_ID, this.sessionId_);
  }

  /**
   * retry mechanism on sideband metadata failures.
   * @returns void
   */
  private retryOnError(): void {
    if (this.retryCount === 0) {
      this.player_ &&
        this.player_.send(
          StreamTimelineEvent.TRITON_SBM_UNAVAILABLE,
          Action.NONE,
          createError(
            TritonErrorCodes.SERVER_SENT_EVENT_SOURCE_FAILURE | ErrorCodes.ERROR_CATEGORY_MASK_THIRDPARTY,
            'Sideband metadata connection failure',
          ),
        );
      this.release();
      return;
    }

    const delay = Math.min(
      this.RETRY_DELAY_RANGE_LOWER_BOUNDARY *
        (this.audioStreamConnectionConfig.sidebandMetadataConnectionDelay ?? 1) *
        Math.pow(2, this.retryCount),
      this.RETRY_DELAY_RANGE_UPPER_BOUNDARY * (this.audioStreamConnectionConfig.sidebandMetadataConnectionDelay ?? 1),
    );

    setTimeout(() => this.sseClientAdapter_.init(), delay);
    this.retryCount = this.retryCount - 1;
  }
}
